import React, { useState, useMemo, useContext, memo } from 'react';
import { genAttributes } from '@onward-delivery/core';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { addHours, format } from 'date-fns';
import { css } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { Button, Grid, MenuItem } from '@material-ui/core';
import GoogleMap, { Marker } from '@/components/GoogleMap';
import AddressAutocomplete from '@/components/ShipmentForm/ModifiedAddressAutocomplete';
import { colors } from '@/styles';
import { asBrowserDate, asDateInTZ } from '@/utilities/convertToISO';
import { validate as validatePhone } from '@/utilities/formatPhoneNumber';
import {
    autocompleteFullAddress,
    autocompleteLatLngPatch,
    isResolvedAutocompleteAddress,
    toFullState,
    toShortState,
} from '@/utilities/address';

import {
    addressSplit,
    sanitizeEnum,
    VALID_LOCATION_TYPES,
    VALID_DROPOFF_TYPES as VALID_PICKUP_TYPES,
} from '../../utilities/processOrders';
import StartTimeSelect from '../../../misc/StartTimeSelect';
import { QUERY_LOCATIONS_BY_CLIENT_ID } from '../../graphql/queries';
import { useSwappedAttributes } from '../../hooks';
import { ErrorText, TextField, OnwardToggle, RadioLabelInfo, ToggleBlurb, TabTitle, TabRow } from '../../blocks';
import { ModalContext } from './';

import {
    SingleDatePicker,
    SecondaryContactBtn,
    StoreSelect,
    NewAddressCheckbox,
    LocationType,
    PickupType,
    StoreDetails,
    OnwardTextField,
    OnwardPhoneInput,
} from '../InputFields';
import { useClientUser, useOrderNotes } from '@/hooks';
import {
    autocompleteAddressPatch,
    clearGeocodingLeg,
    formatGeocodingSuggestion,
    geocodingSuggestionCoords,
    hasReviewGeocodingSuggestion,
    isApproximateGeocodingSuggestion,
} from './geocoding';

const ContactFields = ({ order, attrs, hasError, hasWarning, isDirty, callbacks, options }) => {
    const [name, phone, email] = attrs;

    return (
        <TabRow>
            <Grid item xs={4}>
                <OnwardTextField
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={order[name]}
                    onBlur={() => callbacks.makeDirty([name])}
                    onChange={(e) => callbacks.modifyOrder({ [name]: e.target.value })}
                    error={hasError[name] && (options.startDirty || isDirty[name])}
                />
            </Grid>
            <Grid item xs={4}>
                <OnwardPhoneInput
                    debounced={false}
                    fullWidth
                    label="Phone"
                    variant="outlined"
                    value={order[phone]}
                    onBlur={() => callbacks.makeDirty([phone])}
                    onChange={(e, value) => callbacks.modifyOrder({ [phone]: value })}
                    error={hasError[phone] && (options.startDirty || isDirty[phone])}
                    warning={hasWarning[phone]}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={order[email]}
                    onBlur={() => callbacks.makeDirty([email])}
                    onChange={(e) => callbacks.modifyOrder({ [email]: e.target.value })}
                />
            </Grid>
        </TabRow>
    );
};

const PickupTab = memo(() => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { order, isDirty, hasError, hasWarning, options } = modalState;
    const { disableAddressEditing, disableGeocoding, startDirty } = options;
    const { user_id, client_id } = useClientUser();
    const [collapsed, setCollapsed] = useState(false);
    const [hasSecondaryContactOverride, setHasSecondaryContact] = useState(null);
    const [hasNewAddressOverride, setHasNewAddress] = useState(null);
    const [storeOverride, setStoreOverride] = useState(null);

    const clientId = useMemo(() => {
        if ((order.carrier_id && (order.wh_events || []).find((e) => e.action === 'START:AWAITING')) || order.oms) {
            return order.carrier_id;
        }
        return order.shipper_id || client_id;
    }, [order]);

    const { data: locationsData } = useQuery(QUERY_LOCATIONS_BY_CLIENT_ID, {
        variables: {
            client_id: clientId,
        },
        onError: (e) => {
            callbacks.onError(e);
        },
    });

    const {
        is_custom,
        geocode_failed,
        address,
        city,
        state,
        street,
        zip,
        lat,
        long,
        comments,
        unit,
        location,
        location_type,
    } = useSwappedAttributes(order, true);

    const locations = useMemo(() => {
        return locationsData?.locations || [];
    }, [locationsData]);

    const viewMap = useMemo(() => {
        return order[is_custom];
    }, [order, is_custom]);

    const geocodingLeg = useMemo(() => {
        const leg = genAttributes(order, true).geocoding;
        if (!leg) throw new Error('genAttributes() must provide geocoding leg metadata; update @onward-delivery/core.');
        return leg;
    }, [order]);
    const geocodingSuggestion = order?.geocoding?.[geocodingLeg]?.suggestion;
    const hasGeocodingSuggestion = hasReviewGeocodingSuggestion(order?.geocoding, geocodingLeg);
    const suggestionLabel = formatGeocodingSuggestion(geocodingSuggestion);
    const suggestionCoords = geocodingSuggestionCoords(geocodingSuggestion);
    const isApproximateSuggestion = isApproximateGeocodingSuggestion(geocodingSuggestion);
    const suggestionPrefix = isApproximateSuggestion ? 'Approximate suggested map location' : 'Suggested map location';
    const suggestionAction = isApproximateSuggestion ? 'Use approximate pin' : 'Use suggested pin';
    const clearCurrentGeocoding = () => clearGeocodingLeg(order.geocoding, geocodingLeg);
    const autocompletePatch = (value, split) =>
        autocompleteAddressPatch({
            attrs: { geocode_failed, address, street, city, state, zip, lat, long },
            geocoding: order.geocoding,
            leg: geocodingLeg,
            value,
            split,
        });
    const applySuggestedPin = () => {
        callbacks.modifyOrder({
            [lat]: geocodingSuggestion.lat,
            [long]: geocodingSuggestion.lng,
            [is_custom]: true,
            [geocode_failed]: false,
            auto_assign_failed: false,
            geocoding: clearCurrentGeocoding(),
        });
        callbacks.makeDirty([lat, long, is_custom, geocode_failed]);
    };
    const manualPinPatch = (nextIsCustom = true) => ({
        [is_custom]: nextIsCustom,
        [geocode_failed]: false,
        ...(nextIsCustom && hasGeocodingSuggestion ? { [lat]: null, [long]: null } : {}),
        ...(!nextIsCustom || !hasGeocodingSuggestion ? { geocoding: clearCurrentGeocoding() } : {}),
    });

    const hasSecondaryContact = useMemo(() => {
        return hasSecondaryContactOverride !== null
            ? hasSecondaryContactOverride
            : order?.secondary_pickup_contact_name?.length > 0;
    }, [order, hasSecondaryContactOverride]);

    const store = useMemo(() => {
        if (!storeOverride && locations.length) {
            const match = locations.findIndex(
                (location) =>
                    location.address === order[address] ||
                    (location.location_name && location.location_name === order.chosen_store_name)
            );
            return match > -1 ? match : null;
        }
        return storeOverride;
    }, [storeOverride, order, locations]);

    const hasNewAddress = useMemo(() => {
        let hasNewAddress = false;
        if (hasNewAddressOverride !== null) {
            hasNewAddress = hasNewAddressOverride;
        } else {
            hasNewAddress =
                (locations?.length || 0) === 0 ||
                ((order[address]?.length || 0) > 0 && store === null) ||
                hasError[location] ||
                hasError[location_type] ||
                order[is_custom];
        }

        return hasNewAddress;
    }, [hasNewAddressOverride, order, store, hasError, location, location_type, is_custom, locations]);

    const coords = useMemo(() => {
        if (order && lat && long) {
            return order[lat] && order[long] ? { lat: order[lat], lng: order[long] } : null;
        }

        return null;
    }, [order, lat, long]);
    const mapCenter = coords || suggestionCoords;

    const allowCustom = useMemo(() => {
        return order[is_custom] || order[lat] || order[long] || order[geocode_failed] || hasGeocodingSuggestion;
    }, [order, is_custom, lat, long, geocode_failed, hasGeocodingSuggestion]);

    const orderTZ = useMemo(() => {
        return order[zip] ? zipcode_to_timezone.lookup(order[zip]) : Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, [order, zip]);

    const handleStoreSelect = async (e) => {
        setStoreOverride(e.target.value);
        let _store = locations[e.target.value];
        const [address1, address2] = addressSplit(_store.business_address);

        const patch = {
            pickup_name: _store.contact_name || order.pickup_name,
            pickup_email: _store.contact_email || order.pickup_email,
            pickup_phone: validatePhone(_store.contact_phone) ? _store.contact_phone : order.pickup_phone,
            chosen_store_name: _store.location_name,
            [is_custom]: false,
            [unit]: address2,
            [location]: sanitizeEnum(_store.location_type, VALID_PICKUP_TYPES) || 'Business',
            [location_type]: sanitizeEnum(_store.location_info, VALID_LOCATION_TYPES) || 'dock',
            geocoding: clearCurrentGeocoding(),
        };

        if (disableGeocoding) {
            callbacks.modifyOrder({
                ...patch,
                [geocode_failed]: false,
                [address]: [address1, _store.business_city, _store.business_state, _store.business_zip].join(', '),
                [street]: address1,
                [city]: _store.business_city,
                [state]: _store.business_state,
                [zip]: _store.business_zip,
            });
        } else {
            const results = await callbacks.enrichOrder(
                address,
                [
                    address1,
                    ...(_store.business_city ? [_store.business_city] : []),
                    ...(_store.business_state ? [_store.business_state] : []),
                    ...(_store.business_zip ? [_store.business_zip] : []),
                ].join(', '),
                { pickup: true }
            );
            if (results.errors.geocodeFailed) {
                callbacks.onError(new Error('Geocode failed. Please try again.'));
            }

            callbacks.modifyOrder(patch);
        }

        callbacks.makeDirty([is_custom, unit, address, street, city, state, zip]);
    };

    const hasPickupAddressIssue =
        [address, street, city, state, zip, lat, long].some(
            (attr) => hasError[attr] && (startDirty || isDirty[attr])
        ) || order[geocode_failed];

    let addressError = null;
    if (order.auto_assign_failed) {
        addressError = (
            <Grid
                css={css`
                    color: ${colors.reds[1]};
                    margin-bottom: 4px;
                `}
                container
                direction="row"
            >
                <ErrorText>Address provided could not be auto assigned to carrier</ErrorText>
            </Grid>
        );
    } else if ((allowCustom && hasNewAddress && hasPickupAddressIssue) || hasGeocodingSuggestion) {
        const message = hasPickupAddressIssue ? 'Address is invalid' : 'Address needs review';
        addressError = (
            <Grid
                css={css`
                    color: ${colors.reds[1]};
                    margin-bottom: 4px;
                `}
                container
                direction="column"
            >
                <ErrorText>{message}</ErrorText>
                {hasGeocodingSuggestion ? (
                    <Grid
                        item
                        css={css`
                            margin-top: 4px;
                        `}
                    >
                        {suggestionPrefix}: {suggestionLabel || 'approximate location'}
                        <Button
                            color="primary"
                            disabled={disableAddressEditing}
                            onClick={applySuggestedPin}
                            size="small"
                        >
                            {suggestionAction}
                        </Button>
                    </Grid>
                ) : null}
            </Grid>
        );
    } else if (order.distance_failed) {
        // distance/miles/duration_seconds aren't in hasError (operator can't
        // fix route metrics), but distance_failed still flags a real routing
        // failure — surface the banner so it isn't silently dropped.
        addressError = (
            <Grid
                css={css`
                    color: ${colors.reds[1]};
                    margin-bottom: 4px;
                `}
                container
                direction="row"
            >
                <ErrorText>Failed to find directions between pickup and dropoff address</ErrorText>
            </Grid>
        );
    }

    const isImport = window.location.pathname === '/import' || window.location.pathname.includes('job');
    const userType = (() => {
        if (!isImport && order?.carrier_id && client_id !== order?.shipper_id) {
            return 'Carrier';
        } else {
            return 'Shipper';
        }
    })();
    const orderNotes = useOrderNotes([order], userType);
    const currentNote = orderNotes?.pickup?.[0] || {};

    const modifyInitialNote = (e) => {
        if (currentNote?.note_id) {
            const currentNotes = [...order.notes];
            const currentNoteIdx = currentNotes.findIndex((n) => n.note_id === currentNote.note_id);
            currentNotes[currentNoteIdx] = {
                ...currentNote,
                note: e.target.value,
            };
            callbacks.modifyOrder({ notes: currentNotes });
        } else {
            callbacks.addNote({
                note: e.target.value,
                source_user_type: userType,
                source_user_id: user_id,
                is_acknowledgement: false,
                requires_acknowledgement: false,
                acknowledged_by_shipper: false,
                acknowledged_by_admin: false,
                private_to: null,
                type: 'Pickup',
            });
        }
    };

    return (
        <>
            <TabTitle collapsed={collapsed} callbacks={{ setCollapsed }}>
                Pickup
            </TabTitle>
            {collapsed ? null : (
                <>
                    <ContactFields
                        order={order}
                        isDirty={isDirty}
                        attrs={['pickup_name', 'pickup_phone', 'pickup_email']}
                        hasError={hasError}
                        hasWarning={hasWarning}
                        callbacks={callbacks}
                        options={options}
                    />

                    <TabRow>
                        <Grid item>
                            <SecondaryContactBtn
                                hasSecondaryContact={hasSecondaryContact}
                                onClick={() => {
                                    setHasSecondaryContact(!hasSecondaryContact);
                                    callbacks.modifyOrder({
                                        secondary_pickup_contact_name: null,
                                        secondary_pickup_contact_phone: null,
                                        secondary_pickup_contact_email: null,
                                    });
                                }}
                            />
                        </Grid>
                    </TabRow>

                    {hasSecondaryContact && (
                        <ContactFields
                            order={order}
                            isDirty={isDirty}
                            attrs={[
                                'secondary_pickup_contact_name',
                                'secondary_pickup_contact_phone',
                                'secondary_pickup_contact_email',
                            ]}
                            hasError={hasError}
                            hasWarning={hasWarning}
                            callbacks={callbacks}
                            options={options}
                        />
                    )}

                    <TabRow>
                        <Grid item xs={9}>
                            <StoreSelect
                                value={store}
                                onChange={handleStoreSelect}
                                error={[address, street, city, state, zip, lat, long].some(
                                    (attr) => hasError[attr] && (startDirty || isDirty[attr])
                                )}
                                storeList={locations || []}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <NewAddressCheckbox
                                hasNewAddress={hasNewAddress}
                                disabled={locations?.length === 0}
                                onChange={() => {
                                    const next = !hasNewAddress;
                                    if (!next) {
                                        callbacks.modifyOrder({
                                            [is_custom]: false,
                                            geocoding: clearCurrentGeocoding(),
                                        });
                                    }
                                    setHasNewAddress(next);
                                }}
                            />
                        </Grid>
                    </TabRow>

                    {addressError ? addressError : null}

                    {!hasNewAddress && store !== null ? (
                        <>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            >
                                <StoreDetails
                                    storeName={locations?.[store]?.location_name}
                                    fullAddress={order[address]}
                                    locationType={order[location]}
                                    pickupType={order[location_type]}
                                />
                            </Grid>
                            <TabRow>
                                <Grid item xs={12}>
                                    <TextField
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        label="Comments"
                                        multiline={true}
                                        value={currentNote?.note || ''}
                                        onChange={modifyInitialNote}
                                    />
                                </Grid>
                            </TabRow>
                        </>
                    ) : (
                        <>
                            <TabRow>
                                <Grid item xs={9}>
                                    {order[is_custom] ? (
                                        <OnwardTextField
                                            variant="outlined"
                                            color="primary"
                                            label="Address"
                                            value={order[street]}
                                            error={hasError[street] && (startDirty || isDirty[street])}
                                            disabled={disableAddressEditing}
                                            onBlur={() => callbacks.makeDirty([street])}
                                            onChange={(e) => {
                                                callbacks.modifyOrder({
                                                    [street]: e.target.value,
                                                    [geocode_failed]: false,
                                                    auto_assign_failed: false,
                                                    geocoding: clearCurrentGeocoding(),
                                                });
                                                callbacks.makeDirty([address, street]);
                                            }}
                                            fullWidth
                                        />
                                    ) : (
                                        <AddressAutocomplete
                                            disabled={disableAddressEditing}
                                            state={{
                                                street: order[street],
                                                city: order[city],
                                                state: order[state],
                                                zip: order[zip],
                                            }}
                                            handleAddressUpdate={async (value, split) => {
                                                if (disableGeocoding) {
                                                    // Strong coords when geocoding is off
                                                    callbacks.modifyOrder({
                                                        ...autocompletePatch(value, split),
                                                        ...autocompleteLatLngPatch(split, lat, long),
                                                    });
                                                } else if (!isResolvedAutocompleteAddress(split)) {
                                                    callbacks.modifyOrder(autocompletePatch(value, split));
                                                } else {
                                                    // Seed enrich() with placeId result
                                                    const results = await callbacks.enrichOrder(
                                                        address,
                                                        autocompleteFullAddress(value, split),
                                                        {
                                                            pickup: true,
                                                            geocodeOverride: split,
                                                        }
                                                    );
                                                    if (results.errors.geocodeFailed) {
                                                        callbacks.onError(
                                                            new Error('Geocode failed. Please try again.')
                                                        );
                                                    }
                                                    callbacks.modifyOrder({ geocoding: clearCurrentGeocoding() });
                                                }

                                                callbacks.makeDirty([address, street, city, state, zip]);
                                            }}
                                            error={
                                                (hasNewAddress &&
                                                    [
                                                        address,
                                                        street,
                                                        city,
                                                        state,
                                                        zip,
                                                        lat,
                                                        long,
                                                        'distance',
                                                        'miles',
                                                        'duration_seconds',
                                                    ].some(
                                                        (attr) => hasError[attr] && (startDirty || isDirty[attr])
                                                    )) ||
                                                order[geocode_failed] ||
                                                order.auto_assign_failed
                                            }
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={3}>
                                    <OnwardTextField
                                        variant="outlined"
                                        color="primary"
                                        label="Unit/Suite #"
                                        value={order[unit]}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                [unit]: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                        fullWidth
                                    />
                                </Grid>
                            </TabRow>
                            <TabRow>
                                <Grid item xs={4}>
                                    <OnwardTextField
                                        fullWidth
                                        label="City"
                                        variant="outlined"
                                        color="primary"
                                        value={order[city]}
                                        error={hasError[city] && (startDirty || isDirty[city])}
                                        onBlur={() => callbacks.makeDirty([city])}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                [city]: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <OnwardTextField
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        label="State"
                                        error={hasError[state] && (startDirty || isDirty[state])}
                                        value={toFullState(order[state])}
                                        onBlur={() => callbacks.makeDirty([state])}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                [state]: toShortState(e.target.value),
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <OnwardTextField
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        label="Zip"
                                        value={order[zip]}
                                        error={hasError[zip] && (startDirty || isDirty[zip])}
                                        onBlur={() => callbacks.makeDirty([zip])}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                [zip]: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                    />
                                </Grid>
                            </TabRow>
                            <TabRow>
                                <Grid item xs={12}>
                                    <TextField
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        label="Comments"
                                        multiline={true}
                                        value={currentNote?.note || ''}
                                        onChange={modifyInitialNote}
                                    />
                                </Grid>
                            </TabRow>
                            {allowCustom ? (
                                <TabRow>
                                    <Grid item>
                                        <OnwardToggle
                                            css={css`
                                                margin: 0;
                                            `}
                                            disabled={disableAddressEditing}
                                            value={order[is_custom] || false}
                                            onChange={(e) => {
                                                const nextIsCustom = e.target.checked;
                                                callbacks.modifyOrder(manualPinPatch(nextIsCustom));
                                                callbacks.makeDirty([
                                                    address,
                                                    street,
                                                    ...(nextIsCustom && hasGeocodingSuggestion ? [lat, long] : []),
                                                ]);
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        direction="column"
                                        container
                                        css={css`
                                            flex: 1;
                                            flex-basis: 0;
                                            margin-left: 12px;
                                            justify-content: center;
                                        `}
                                    >
                                        <Grid container item>
                                            <RadioLabelInfo
                                                css={css`
                                                    color: #000;
                                                    font-weight: 400;
                                                `}
                                            >
                                                Having trouble with your address?&nbsp;
                                                <a
                                                    css={css`
                                                        cursor: pointer;
                                                    `}
                                                    onClick={() => {
                                                        if (disableAddressEditing) return;
                                                        callbacks.modifyOrder(manualPinPatch());
                                                        callbacks.makeDirty([
                                                            address,
                                                            street,
                                                            ...(hasGeocodingSuggestion ? [lat, long] : []),
                                                        ]);
                                                    }}
                                                >
                                                    Place a pin on the map
                                                </a>
                                            </RadioLabelInfo>
                                        </Grid>

                                        {order[is_custom] ? (
                                            <Grid
                                                container
                                                item
                                                css={css`
                                                    margin-top: 8px;
                                                `}
                                            >
                                                <ToggleBlurb />
                                            </Grid>
                                        ) : null}
                                    </Grid>
                                </TabRow>
                            ) : null}
                        </>
                    )}

                    {viewMap && (
                        <Grid
                            item
                            css={css`
                                min-height: 350px;
                                margin-top: 24px;
                                border-radius: 6px;
                                border: ${hasError[lat] || hasError[long]
                                    ? `1px solid #f44336;`
                                    : '1px solid transparent;'};
                            `}
                        >
                            <GoogleMap
                                zoom={15}
                                onClick={(e) => {
                                    if (disableAddressEditing) return;

                                    if (disableGeocoding) {
                                        const x = e.latLng.lat();
                                        const y = e.latLng.lng();

                                        callbacks.modifyOrder({
                                            [lat]: x,
                                            [long]: y,
                                            geocoding: clearCurrentGeocoding(),
                                        });
                                    } else {
                                        callbacks.geocodeLatLong(e);
                                        callbacks.modifyOrder({ geocoding: clearCurrentGeocoding() });
                                    }

                                    callbacks.makeDirty([lat, long]);
                                }}
                                draggableCursor="pointer"
                                gestureHandling="greedy"
                                center={mapCenter || undefined}
                            >
                                {coords ? <Marker position={coords} /> : null}
                                {!coords && suggestionCoords ? (
                                    <Marker
                                        position={suggestionCoords}
                                        opacity={0.7}
                                        label={{ text: '?', color: 'white' }}
                                    />
                                ) : null}
                            </GoogleMap>
                        </Grid>
                    )}

                    <TabRow>
                        <Grid item xs={6}>
                            <LocationType
                                value={order[location]}
                                onChange={(e) => {
                                    callbacks.modifyOrder({ [location]: e.target.value });
                                    callbacks.makeDirty([location]);
                                }}
                                error={hasError[location] && (startDirty || isDirty[location])}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <PickupType
                                disabled={!order[location] || order[location].length === 0}
                                value={order[location_type]}
                                locationType={order[location]}
                                onChange={(e) => {
                                    callbacks.modifyOrder({ [location_type]: e.target.value });
                                    callbacks.makeDirty([location_type]);
                                }}
                                error={hasError[location_type] && (startDirty || isDirty[location_type])}
                            />
                        </Grid>
                    </TabRow>
                    <TabRow>
                        {!options.enablePickupDateEditing ? (
                            <Grid item xs={6}>
                                <SingleDatePicker
                                    label="First Available"
                                    tooltipText="This is the earliest date that these orders can be scheduled for delivery. Will be defaulted to today if left blank."
                                    value={
                                        order.first_available_date
                                            ? format(new Date(order.first_available_date), 'yyyy-MM-dd')
                                            : null
                                    }
                                    onChange={(e) =>
                                        callbacks.modifyOrder({
                                            first_available_date: asBrowserDate(e.target.value).toISOString(),
                                        })
                                    }
                                    error={
                                        hasError.first_available_date && (startDirty || isDirty.first_available_date)
                                    }
                                />
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={4}>
                                    <TextField
                                        select
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        label="Pickup"
                                        value={order.is_same_day}
                                        onChange={(e) => {
                                            const utc = new Date(new Date().setUTCHours(0, 0, 0, 0)).toISOString();
                                            const hour = new Date(
                                                addHours(new Date().setMinutes(0, 0, 0), 1)
                                            ).toISOString();
                                            const today = asDateInTZ(utc, orderTZ).toISOString();

                                            callbacks.modifyOrder({
                                                is_same_day: e.target.value,
                                                ...(e.target.value
                                                    ? {
                                                          delivery_date: today,
                                                          pickup_date: today,
                                                          firstAvailableDate: today,
                                                          pickup_window_end: hour,
                                                      }
                                                    : {
                                                          delivery_date: null,
                                                          pickup_date: null,
                                                          firstAvailableDate: null,
                                                          pickup_window_end: null,
                                                          pickup_window_start: null,
                                                      }),
                                            });
                                        }}
                                    >
                                        <MenuItem key="yes" value={true}>
                                            Now
                                        </MenuItem>
                                        <MenuItem key="no" value={false}>
                                            Later
                                        </MenuItem>
                                    </TextField>
                                </Grid>
                                {!order.is_same_day ? (
                                    <>
                                        <Grid item xs={4}>
                                            <SingleDatePicker
                                                label="First Available"
                                                tooltipText="This is the earliest date that these orders can be scheduled for delivery. Will be defaulted to today if left blank."
                                                value={
                                                    order.first_available_date
                                                        ? format(
                                                              asBrowserDate(order.first_available_date),
                                                              'yyyy-MM-dd'
                                                          )
                                                        : null
                                                }
                                                onChange={(e) => {
                                                    let dateStr = null;
                                                    let hour = null;

                                                    if (e.target.value) {
                                                        const utc = new Date(
                                                            new Date(e.target.value).setUTCHours(0, 0, 0, 0)
                                                        ).toISOString();
                                                        dateStr = asDateInTZ(utc, orderTZ).toISOString();

                                                        hour = addHours(
                                                            new Date(new Date(dateStr).setUTCMinutes(0, 0, 0)),
                                                            8
                                                        ).toISOString();
                                                    }

                                                    callbacks.modifyOrder({
                                                        first_available_date: dateStr,
                                                        delivery_date: dateStr,
                                                        pickup_window_end: hour ? hour : null,
                                                    });
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <StartTimeSelect
                                                onChange={(e) => {
                                                    callbacks.modifyOrder({
                                                        pickup_window_end: new Date(e.target.value).toISOString(),
                                                    });
                                                }}
                                                value={
                                                    order.pickup_window_end
                                                        ? new Date(order.pickup_window_end).getTime()
                                                        : 0
                                                }
                                                timeZone={orderTZ}
                                                deliveryDate={
                                                    order.first_available_date
                                                        ? order.first_available_date
                                                        : new Date().toISOString()
                                                }
                                                disabled={!order.first_available_date}
                                                interval={30}
                                                label="Pickup Time"
                                                filter={({ local }) => {
                                                    const hours = local.getHours();
                                                    const minutes = local.getMinutes();
                                                    return (
                                                        hours >= 6 && (hours < 20 || (hours === 20 && minutes === 0))
                                                    );
                                                }}
                                                InputLabelProps={{
                                                    shrink: !!order.pickup_window_end,
                                                }}
                                            />
                                        </Grid>
                                    </>
                                ) : null}
                            </>
                        )}
                    </TabRow>
                </>
            )}
        </>
    );
});

export default PickupTab;
